/**
 * Fiche cours v2 — routes class/:id et class/:id/enrollment (SPEC § 4.2 : FC1 à FC3, FC6, FC7, FC9 à
 * FC17 ; retours client R1, R2, R3, R5).
 *
 * Données : hooks v1 (`useFetchClass`, `useFetchClassWaitlistEntry`, `useListClientServices`,
 * `useListVisits`, mêmes clés de cache) ; réservation : `useBooking` (logique v1 reproduite) ;
 * visiteur déconnecté ou sans crédit : `usePublicClass` (plan public, le plan reste visible).
 *
 * Mise en page (maquette 06 à 14, 26) : bandeau allégé, plan à gauche, panneau collant à droite qui
 * commence par la séance (R1). Jusqu'à 960 px, une colonne : la séance en tête de page, puis le plan,
 * « Venez à deux », l'action (collante en bas une fois le spot choisi, FC15) et « Le cours ».
 */
import { Skeleton, useMediaQuery } from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import { useFetchClass, useFetchClassWaitlistEntry, useListClientServices, useListVisits } from 'hooks/useApiQueries';
import { useUserContext } from 'providers/useUserContext';
import { useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';
import AuthScreen from 'screens/Account/Auth';
import { useV1AuthPatches } from '../Account/useV1AuthPatches';
import { classHasCapacity, getLateCancelHours, isClassBookable } from 'utils/functions';
import { trackViewClass } from 'utils/tracking';
import { cssThemeTokenReader, readSettings, resolveTokens } from '../../theme/tokens';
import { CancelModal } from './components/CancelModal';
import { CourseCard } from './components/CourseCard';
import { Hero } from './components/Hero';
import { useBackToPlanning } from './useBackToPlanning';
import { Icon } from './components/Icon';
import { GuestBox, Line, Notice, ServicePicker, SummaryCard } from './components/Panel';
import { RoomPlan } from './components/RoomPlan';
import { useClassToast } from './components/toast';
import {
  bookingOpensAt,
  creditsRequiredFor,
  effectiveServiceId as effectiveServiceFor,
  eligibleServices,
  EMPTY_SELECTION,
  isCommunityClassName,
  isPastClass,
  isRunClubClass,
  selectSpot,
  toggleGuest,
  type SpotSelection,
} from './lib/booking';
import { cancelSeats } from './lib/cancel';
import { classColors, classColorVars } from './lib/colors';
import {
  addressLine,
  bookedCancelHint,
  coachName,
  freeCancelHint,
  hour,
  longDate,
  opensLabel,
  plural,
  studioLabel,
} from './lib/format';
import { downloadIcs } from './lib/ics';
import { buildPlan, PUNCH_SITE_IDS } from './lib/room';
import { usePublicClass } from './usePublicClass';
import { sessionOf } from './lib/session';
import { locationNameOf, readEventSplit } from '../../lib/planning/eventStudios';
import { useBooking } from './useBooking';
import { prefetchFollowUps } from './useFollowUps';
import { injectV2Css } from '../../styles/inject';
import classCss from './class.css?inline';

injectV2Css('screens/Class/class.css', classCss);

/** = Class/index.tsx : note Dynamo Paris sous le plan. */
const DYNAMO_PARIS_SITE_ID = '5736526';
const FRONT_ROW_NOTE = "Le premier rang est recommandé aux riders réguliers. C’est vous qui fixerez le rythme pendant la session.";
/** SPEC FC2 : phrase exacte au-dessus du plan. */
export const SELECT_SPOT_HINT = 'Sélectionnez votre spot pour confirmer votre réservation.';
/** Consigne du plan quand le client n'a pas de quoi réserver (creditBlocked). */
export const CREDIT_BLOCKED_HINT = 'Crédits insuffisants pour ce cours : achetez des crédits pour réserver votre spot.';
/** Fondu posé au-dessus du dock du bureau (class.css, .mmv2-class-dock::before) : le récapitulatif doit finir avant. */
const DOCK_FADE_PX = 16;

type PanelKind =
  | 'loading'
  | 'loggedOut'
  | 'noCredit'
  | 'error'
  | 'past'
  | 'canceled'
  | 'booked'
  | 'waitlistJoined'
  | 'spotFreed'
  | 'notOpen'
  | 'full'
  | 'waitlistFull'
  | 'bookable';

export interface ConfirmationState {
  spots: number[];
  serviceId?: number;
  isEnrollment: boolean;
  isCourse: boolean;
  /**
   * Heure de la réponse du serveur (Date.now()) : la relecture des crédits est partie à cet instant
   * (useBooking.refreshAfterBooking) ; la confirmation s'y raccroche au lieu de la relancer.
   */
  bookedAt?: number;
}

export function ClassScreenV2() {
  const { id = '' } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  /**
   * = Class/index.tsx (401 ServicePurchaseRequired) : sur l'ancienne page Tarifs, seules les offres utilisables
   * pour CE cours (`<Services classId={id} />`, GET sale/services?classId=). Le cours est transmis à la route
   * tarifs par l'état de navigation ; la page administrable (pricingConfig) l'ignore, comme la v1.
   */
  const buyCredits = () => navigate('/tarifs', { state: { classId: id } });
  const back = useBackToPlanning();
  const toast = useClassToast();
  const isEnrollmentInURL = location.pathname.includes('enrollment');
  const isCourseFromState = (location.state as { isCourse?: boolean } | null)?.isCourse === true;
  const { isLoggedIn, user } = useUserContext();

  const colorVars = useMemo(() => {
    try {
      return classColorVars(classColors(resolveTokens(readSettings(), cssThemeTokenReader())));
    } catch {
      return {};
    }
  }, []);

  /* ---------------------------------------------------------------- données */

  const queryClient = useQueryClient();
  const classQuery = useFetchClass(id, isEnrollmentInURL);
  const { data: waitlistEntry } = useFetchClassWaitlistEntry(id);
  const { data: servicesData } = useListClientServices();
  const { data: visits } = useListVisits() || {};

  const noCredit = classQuery.error?.ErrorCode === 'ServicePurchaseRequired';
  const pub = usePublicClass(id, !isLoggedIn || noCredit);

  const mindbodyClass = classQuery.data;
  const cls = mindbodyClass ?? pub.cls;

  // Tunnel de réservation : fiche affichée (connecté ou non).
  useEffect(() => {
    if (cls) trackViewClass(cls);
  }, [cls]);
  const pick = mindbodyClass ? mindbodyClass.PickASpot : pub.pickASpot;

  const brandName = (typeof mondayMindbody !== 'undefined' && mondayMindbody?.customization?.brandName) || '';
  const siteId = String((typeof mondayMindbody !== 'undefined' && mondayMindbody?.siteId) || '');

  // = Class/index.tsx : drapeaux du cours.
  const isCourse = !!(mindbodyClass?.IsCourse || isCourseFromState);
  const hasNoRoomLayout = !pick;
  const creditsRequired = creditsRequiredFor(cls);
  const isMultiCreditClass = creditsRequired > 1;
  const isCommunityClass = isCommunityClassName(cls?.ClassDescription?.Name);
  const isRunClub = isRunClubClass(cls, siteId);
  const classIsFull = cls ? !classHasCapacity(cls) : false;
  const isWaitlistFull = cls ? cls.TotalBookedWaitlist >= cls.WaitListSize : false;
  const freeOfCharge = isCommunityClass || isRunClub;
  const lateCancelHours = getLateCancelHours(cls?.Location?.SiteID ?? null);

  const visitInformations = useMemo(() => visits?.find((visit) => visit.ClassId === Number(id)), [visits, id]);
  const bookedVisits = useMemo(
    () =>
      (visits ?? []).filter(
        (visit) => visit.ClassId === Number(id) && visit.AppointmentStatus === 'Booked' && visit.ClientId === user?.Id,
      ),
    [visits, id, user?.Id],
  );

  /* ---------------------------------------------------------------- état local */

  const [selection, setSelection] = useState<SpotSelection>(EMPTY_SELECTION);
  const [chosenServiceId, setChosenServiceId] = useState<number>();
  /** Cours réservé : ajout d'un invité (2e place v1), ou reprise après l'échec du 2e appel. */
  const [addGuest, setAddGuest] = useState<{ spot: number | null; retryAfter?: number } | null>(null);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const planRef = useRef<HTMLDivElement>(null);
  const asideRef = useRef<HTMLElement>(null);
  const authRef = useV1AuthPatches();
  const actionSlotRef = useRef<HTMLDivElement>(null);
  const dockRef = useRef<HTMLDivElement>(null);
  /*
   * Une colonne (≤ 960 px) : barre d'action collante en bas d'écran (FC15). Au bureau, la carte d'action reste à sa
   * place dans le panneau ; un rappel compact (« dock ») ne s'affiche que tant qu'elle est sous la ligne de flottaison
   * (revue du 05/10 : la carte collante recouvrait le haut du panneau dès l'arrivée et cachait les encadrés d'erreur).
   */
  const [narrow] = useMediaQuery('(max-width: 960px)', { ssr: false });
  const [actionBelow, setActionBelow] = useState(false);
  /** Bureau : le récapitulatif « Votre réservation » finit au-dessus du dock (rien de lui sous le rappel). */
  const [summaryClear, setSummaryClear] = useState(true);

  const booking = useBooking({
    id,
    mindbodyClass,
    isEnrollmentInURL,
    isCourse,
    isRunClub,
    isCommunityClass,
    hasNoRoomLayout,
    classIsFull,
    creditsRequired,
    waitlistEntry,
  });
  const { setHasAlreadyUsedRoutineService } = booking;

  // = Class/index.tsx : un forfait challenge déjà utilisé sur ce cours masque les forfaits challenge.
  useEffect(() => {
    if (visitInformations && visitInformations.Service?.Name?.toLowerCase().includes('challenge')) {
      setHasAlreadyUsedRoutineService(true);
    }
  }, [visitInformations, setHasAlreadyUsedRoutineService]);

  const services = useMemo(
    () => eligibleServices(servicesData ? Object.values(servicesData) : [], booking.hasAlreadyUsedRoutineService),
    [servicesData, booking.hasAlreadyUsedRoutineService],
  );

  /* ---------------------------------------------------------------- plan */

  const isPunch = PUNCH_SITE_IDS.includes(siteId);
  const isBookedFromClass =
    !!mindbodyClass &&
    (isEnrollmentInURL || hasNoRoomLayout || isCourse
      ? bookedVisits.length > 0
      : !!pick?.reservations?.find((reservation) => reservation.memberExternalId === user?.Id));

  const past = cls ? isPastClass(cls.StartDateTime) : false;
  const inWindow = cls ? isClassBookable(cls) : false;
  // = Class/index.tsx : la v1 ne signale « pas réservable » que pour les cours à plan, hors enrollment et courses.
  const notOpen = !!cls && !past && !inWindow && !isEnrollmentInURL && !hasNoRoomLayout && !isCourse;

  let kind: PanelKind;
  if (!isLoggedIn) kind = pub.isLoading && !cls ? 'loading' : 'loggedOut';
  else if (classQuery.isLoading || (noCredit && pub.isLoading && !cls)) kind = 'loading';
  else if (noCredit) kind = cls && past ? 'past' : 'noCredit';
  else if (!mindbodyClass) kind = 'error';
  else if (past) kind = 'past';
  else if (mindbodyClass.IsCanceled) kind = 'canceled';
  else if (isBookedFromClass) kind = 'booked';
  else if (waitlistEntry && classIsFull) kind = 'waitlistJoined';
  else if (waitlistEntry) kind = 'spotFreed';
  else if (notOpen) kind = 'notOpen';
  else if (classIsFull) kind = isWaitlistFull ? 'waitlistFull' : 'full';
  else kind = 'bookable';

  const selecting = kind === 'bookable' || kind === 'spotFreed';
  const addingGuest = kind === 'booked' && !!addGuest;
  const interactive = !hasNoRoomLayout && (selecting || addingGuest);

  const plan = useMemo(
    () =>
      pick
        ? buildPlan(pick, {
            userId: user?.Id,
            selected: selecting ? selection.spot : null,
            guest: addingGuest ? addGuest?.spot ?? null : selecting && selection.guestOn ? selection.guestSpot : null,
            isPunch,
            classIsFull,
          })
        : null,
    [pick, user?.Id, selecting, selection, addingGuest, addGuest, isPunch, classIsFull],
  );

  // Un spot choisi qui vient d'être pris (plan relu) n'est plus sélectionné.
  useEffect(() => {
    if (!plan) return;
    const stateOf = (n: number | null) => (n === null ? null : plan.cells.find((c) => c.spotNumber === n)?.state);
    if (selection.spot !== null && stateOf(selection.spot) === 'taken') setSelection((s) => ({ ...s, spot: null }));
    if (selection.guestSpot !== null && stateOf(selection.guestSpot) === 'taken') setSelection((s) => ({ ...s, guestSpot: null }));
    if (addGuest?.spot != null && stateOf(addGuest.spot) === 'taken') setAddGuest((g) => (g ? { ...g, spot: null } : g));
  }, [plan, selection.spot, selection.guestSpot, addGuest?.spot]);

  const mySpots = plan?.mySpots ?? [];

  /* ---------------------------------------------------------------- crédits */

  const guestOffered =
    kind === 'bookable' && !hasNoRoomLayout && !isCourse && !isEnrollmentInURL && !isRunClub && !isCommunityClass && (plan?.freeSpotCount ?? 0) >= 2;
  // SPEC FC7 : crédits ≥ 2 × creditsRequired vérifiés AVANT. Aucun forfait ne paie deux places : l'interrupteur est
  // désactivé avec sa raison, et la réservation solo reste proposée (revue du 04/10 : activé avec 1 crédit, le bouton
  // de confirmation disparaissait derrière « Crédits insuffisants »).
  const guestCredits = creditsRequired * 2;
  const bestRemaining = services.reduce((max, s) => Math.max(max, s.Remaining ?? 0), 0);
  const guestDisabledReason =
    guestOffered && !freeOfCharge && services.length > 0 && bestRemaining < guestCredits
      ? `Il vous faut ${plural(guestCredits, 'crédit')} pour venir à deux\u00a0; vous en avez ${bestRemaining}.`
      : null;
  const guestAllowed = guestOffered && !guestDisabledReason;
  const guestOn = guestAllowed && selection.guestOn;
  const places = guestOn ? 2 : 1;
  const needed = creditsRequired * places;
  const effectiveServiceId = effectiveServiceFor(services, chosenServiceId, needed);
  const selectedService = services.find((s) => Number(s.Id) === effectiveServiceId);
  const remaining = selectedService?.Remaining ?? 0;
  const enoughCredits = freeOfCharge || remaining >= needed;
  const showServicePicker = !freeOfCharge && services.length > 0;
  /**
   * Pas de quoi réserver : Mindbody n'accepte aucun crédit du client (noCredit), ou le forfait n'en a pas assez. Sur
   * téléphone, la carte « Crédits insuffisants » passe alors AU-DESSUS du plan (class.css, data-credit) et la consigne
   * du plan le dit : en dessous, on touchait les spots sans comprendre pourquoi rien ne se passait (retours de Ségolène
   * du 13/10).
   */
  const creditBlocked =
    kind === 'noCredit' || ((kind === 'bookable' || kind === 'spotFreed') && !enoughCredits && !booking.isSubmitting);

  /* ---------------------------------------------------------------- actions */

  /** Confirmation dès la réponse du serveur ; crédits relus en arrière-plan (useBooking, en-tête). */
  const goConfirmation = (spots: number[], serviceId?: number) => {
    const bookedAt = booking.refreshAfterBooking();
    const state: ConfirmationState = { spots, serviceId, isEnrollment: isEnrollmentInURL, isCourse, bookedAt };
    navigate(`/class/${id}/confirmation`, { state });
  };
  /**
   * Un parcours de réservation à la fois. Entre les deux appels de « Venez à deux », isSubmitting repasse un instant à
   * false : sans ce verrou, un second clic pendant ce battement relancerait le parcours.
   */
  const inFlight = useRef(false);

  const focusPlan = () => {
    const el = planRef.current;
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    const firstFree = el.querySelector<HTMLButtonElement>('.mmv2-spot:not(:disabled)');
    firstFree?.focus({ preventScroll: true });
  };

  const onSpot = (n: number) => {
    booking.setError(null);
    if (addingGuest) {
      setAddGuest((g) => (g ? { ...g, spot: g.spot === n ? null : n } : g));
      return;
    }
    setSelection((s) => selectSpot(guestAllowed ? s : { ...s, guestOn: false }, n));
  };

  const missingReason = (() => {
    if (kind !== 'bookable' && kind !== 'spotFreed') return null;
    if (!hasNoRoomLayout && selection.spot === null) return 'Choisissez votre spot sur le plan.';
    if (guestOn && selection.guestSpot === null) return 'Choisissez le spot de votre invité sur le plan.';
    return null;
  })();

  const onConfirm = async () => {
    if (booking.isSubmitting || inFlight.current) return;
    if (missingReason) {
      focusPlan();
      return;
    }
    inFlight.current = true;
    // Suites de la confirmation (CO4, CO5) lues pendant la réservation, sans la retarder (useFollowUps).
    prefetchFollowUps(queryClient, mindbodyClass);
    try {
      // Réussite = confirmation : rien n'est relu avant de l'afficher (refresh 'defer').
      const first = await booking.bookSeat({ spot: selection.spot, serviceId: effectiveServiceId }, 'member', { refresh: 'defer' });
      if (!first.ok) return;
      const mine = selection.spot !== null ? [selection.spot] : [];
      if (guestOn && selection.guestSpot !== null) {
        // SPEC FC7 : 2e appel successif, le serveur le passe en Guest (785fce2). Les deux spots restent choisis sur le
        // plan pendant les deux appels (un seul « Réservation en cours… ») ; le cours n'est relu qu'en cas d'échec.
        const guestSpot = selection.guestSpot;
        const second = await booking.bookSeat({ spot: guestSpot, serviceId: first.serviceId }, 'guest', {
          refresh: 'defer',
          afterBookedSeat: true,
        });
        if (!second.ok) {
          // 1re place réservée (cours relu par l'échec) : étape « invité » avec « Réessayer pour l'invité ».
          setAddGuest({ spot: guestSpot, retryAfter: mine[0] });
          setSelection(EMPTY_SELECTION);
          return;
        }
        goConfirmation([...mine, guestSpot], first.serviceId);
        return;
      }
      goConfirmation(mine, first.serviceId);
    } finally {
      inFlight.current = false;
    }
  };

  const onConfirmGuest = async () => {
    if (!addGuest || booking.isSubmitting || inFlight.current) return;
    if (addGuest.spot === null) {
      focusPlan();
      return;
    }
    inFlight.current = true;
    prefetchFollowUps(queryClient, mindbodyClass);
    try {
      const spot = addGuest.spot;
      const result = await booking.bookSeat({ spot, serviceId: effectiveServiceId }, 'guest', { refresh: 'defer' });
      if (result.ok) {
        setAddGuest(null);
        goConfirmation([...mySpots.map((s) => s.spotNumber), spot], result.serviceId);
      }
    } finally {
      inFlight.current = false;
    }
  };

  const onJoinWaitlist = async () => {
    const result = await booking.bookSeat({ serviceId: effectiveServiceId });
    // Le panneau affiche ensuite l'encadré vert « Vous êtes sur la liste d’attente… » : le toast ne le répète pas (revue du 05/10).
    if (result.ok) toast('Inscription sur la liste d’attente confirmée.', 'mmv2-waitlist');
  };

  const onLeaveWaitlist = async () => {
    setLeaving(true);
    const left = await booking.leaveWaitlist();
    setLeaving(false);
    if (left) toast('Vous avez quitté la liste d’attente.', 'mmv2-waitlist-left');
  };

  const addToCalendar = () => {
    if (!cls) return;
    downloadIcs({
      uid: `class-${cls.Id}-${siteId}@monday-mindbody`,
      start: cls.StartDateTime,
      end: cls.EndDateTime,
      summary: `${cls.ClassDescription?.Name ?? 'Cours'} — ${studioLabel(locationNameOf(cls, readEventSplit()), brandName)}`,
      location: addressLine(cls.Location),
      description: [
        coachName(cls.Staff) ? `Coach\u00a0: ${coachName(cls.Staff)}` : '',
        mySpots.length ? `Spot${mySpots.length > 1 ? 's' : ''}\u00a0: ${mySpots.map((s) => `#${s.spotNumber}`).join(', ')}` : '',
      ]
        .filter(Boolean)
        .join('. '),
      url: window.location.href,
    });
  };

  /* ---------------------------------------------------------------- rendu */

  // Studio virtuel du studio Monday Event de Paris (« Run Club », Special Events), comme au planning.
  const eventSplit = readEventSplit();
  const session = sessionOf(cls, pick, brandName, eventSplit);
  const studio = studioLabel(cls ? locationNameOf(cls, eventSplit) : undefined, brandName);
  // Studio au nom de la marque (« Punch Annecy ») : une seule fois (retour de Morgane, 22/10).
  const heroKicker = [brandName, studio]
    .filter((part, i, parts) => Boolean(part) && (i === 0 || part?.trim().toLowerCase() !== parts[0]?.trim().toLowerCase()))
    .join(' · ');
  const heroTitle = cls?.ClassDescription?.Name ?? (kind === 'error' ? 'Cours introuvable' : 'Réserver un cours');

  // Action à garder en vue (FC15) : spot choisi (réservation, ou spot de l'invité à ajouter).
  const stickyAction =
    (selecting && !hasNoRoomLayout && selection.spot !== null && (enoughCredits || booking.isSubmitting)) ||
    (addingGuest && addGuest?.spot != null);
  /** Une colonne : la carte d'action devient la barre collante du bas d'écran. */
  const barSticky = stickyAction && narrow;
  /**
   * Bureau : rappel compact tant que la carte d'action est sous la ligne de flottaison, et seulement si le
   * récapitulatif n'est pas dessous.
   */
  const dockWanted = stickyAction && !narrow;
  const dockShown = dockWanted && actionBelow && summaryClear;

  /*
   * Bureau : la carte d'action est-elle sous la ligne de flottaison ? (bouton de confirmation pas entièrement visible
   * et plus bas que le milieu de l'écran). Le rappel compact (dock) n'est affiché que dans ce cas : il disparaît dès que
   * le bouton du panneau est en vue, jamais deux boutons de confirmation à l'écran.
   */
  useEffect(() => {
    if (!dockWanted || typeof IntersectionObserver === 'undefined') {
      setActionBelow(false);
      return undefined;
    }
    const button = actionSlotRef.current?.querySelector<HTMLElement>('.mmv2-class-confirm');
    if (!button) return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => {
        const rootHeight = entry.rootBounds?.height ?? window.innerHeight;
        setActionBelow(entry.intersectionRatio < 0.99 && entry.boundingClientRect.top > rootHeight / 2);
      },
      { threshold: [0, 0.25, 0.5, 0.75, 0.99, 1], rootMargin: '0px 0px -16px 0px' },
    );
    observer.observe(button);
    return () => observer.disconnect();
  }, [dockWanted, kind, addingGuest]);

  /*
   * Dock : sur la largeur du panneau (position fixe, sans effet sur la mise en page ni la hauteur du panneau). Monté tant
   * qu'un spot est choisi, montré ou caché par un attribut (pas de remontage à chaque passage de la ligne de flottaison).
   */
  useLayoutEffect(() => {
    const dock = dockRef.current;
    const aside = asideRef.current;
    if (!dockWanted || !dock || !aside) return undefined;
    const place = () => {
      const rect = aside.getBoundingClientRect();
      dock.style.left = `${Math.round(rect.left)}px`;
      dock.style.width = `${Math.round(rect.width)}px`;
    };
    place();
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(place) : null;
    observer?.observe(aside);
    window.addEventListener('resize', place);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', place);
    };
  }, [dockWanted]);

  /*
   * Bureau : le dock ne se pose jamais sur le récapitulatif « Votre réservation » (spot, crédits utilisés et
   * restants, forfait proposé ; cadre « C'est réservé » pendant l'ajout d'un invité). Il n'est montré que si le bas
   * de la carte est au-dessus de son bord haut, fondu compris ; sinon le récapitulatif reste lisible en entier et le
   * bouton du panneau arrive en faisant défiler (revue r4 : à 1440 × 900, la liste des forfaits et le forfait débité
   * étaient sous le dock ; à 1280 et 1024, les lignes Spot et Crédits ; le cadre vert de l'ajout d'invité paraissait
   * ouvert en bas). Mesure au défilement (une par image) : le bord du récapitulatif est comparé au pixel près, ce
   * qu'un seuil d'IntersectionObserver ne garantit pas.
   */
  useLayoutEffect(() => {
    const dock = dockRef.current;
    const aside = asideRef.current;
    if (!dockWanted || !dock || !aside) {
      setSummaryClear(true);
      return undefined;
    }
    let frame = 0;
    const measure = () => {
      frame = 0;
      const summary = aside.querySelector<HTMLElement>('.mmv2-class-slot--summary');
      const limit = dock.getBoundingClientRect().top - DOCK_FADE_PX;
      setSummaryClear(!summary || summary.getBoundingClientRect().bottom <= limit);
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(measure);
    };
    measure();
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(schedule) : null;
    observer?.observe(aside);
    observer?.observe(dock);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      observer?.disconnect();
    };
  }, [dockWanted, kind, addingGuest]);

  /*
   * Échec d'une réservation (ou de la place de l'invité) : l'encadré d'erreur est amené au centre de l'écran et reçoit
   * le focus (revue du 05/10 : hors de l'écran à toutes les largeurs, le bouton revenait à son état initial sans
   * explication visible).
   */
  const guestRetry = addGuest?.retryAfter;
  useEffect(() => {
    if (!booking.error && guestRetry === undefined) return;
    const alert = document.querySelector<HTMLElement>('.mmv2-class-page [data-focus-alert]');
    if (!alert) return;
    const reduce = typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    alert.scrollIntoView({ block: 'center', behavior: reduce ? 'auto' : 'smooth' });
    alert.focus({ preventScroll: true });
  }, [booking.error, guestRetry]);

  /*
   * Panneau collant (bureau) : sa hauteur en variable CSS, pour qu'il colle par le bas quand il dépasse la fenêtre
   * (class.css, .mmv2-class-aside).
   */
  useLayoutEffect(() => {
    const aside = asideRef.current;
    if (!aside || typeof ResizeObserver === 'undefined') return undefined;
    const set = () => aside.style.setProperty('--mmv2-aside-h', `${Math.ceil(aside.getBoundingClientRect().height)}px`);
    set();
    const observer = new ResizeObserver(set);
    observer.observe(aside);
    return () => observer.disconnect();
  }, [kind]);

  /*
   * Barre d'action collante (une colonne) ou dock (bureau) : le défilement au focus clavier s'arrête au-dessus d'eux
   * (scroll-padding-bottom = leur hauteur + leur écart au bas de l'écran + 10 px). Sinon l'interrupteur « Venez à deux »
   * ou la dernière rangée de spots recevaient le focus sous la barre (WCAG 2.4.11, revues du 04/10 et du 05/10).
   */
  useLayoutEffect(() => {
    const target = barSticky ? actionSlotRef.current : dockShown ? dockRef.current : null;
    if (!target) return undefined;
    const html = document.documentElement;
    const before = html.style.scrollPaddingBottom;
    const set = () => {
      const offset = barSticky ? (parseFloat(getComputedStyle(target).getPropertyValue('--m-bar-h')) || 0) + 20 : 26;
      html.style.scrollPaddingBottom = `${Math.ceil(target.getBoundingClientRect().height + offset)}px`;
    };
    set();
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(set) : null;
    observer?.observe(target);
    return () => {
      observer?.disconnect();
      html.style.scrollPaddingBottom = before;
    };
  }, [barSticky, dockShown]);

  if (kind === 'loading') return <ClassLoading style={colorVars} />;

  if (kind === 'error') {
    return (
      <div className="mmv2-class-page" style={colorVars as CSSProperties}>
        <Hero title="Cours introuvable" />
        <div className="mmv2-class-layout mmv2-class-layout--single">
          <section className="mmv2-card mmv2-bp-sect">
            <Notice tone="warm" role="alert">
              <b>Nous n’avons pas pu afficher ce cours.</b>{' '}
              {classQuery.error?.getMessage?.() ?? 'Il a peut-être été retiré du planning.'}
            </Notice>
            <button type="button" className="mmv2-btn mmv2-btn--primary mmv2-btn--block mmv2-class-gap" onClick={() => navigate('/')}>
              Voir le planning
              <Icon name="right" />
            </button>
          </section>
        </div>
      </div>
    );
  }

  /*
   * Spots libres comptés sur le plan (pickaspot, lu en direct), pas sur MaxCapacity - TotalBooked : GET class/{id} lit
   * la grille des cours en cache serveur 45 s (ApiClient, 'class/classes'), et l'étiquette contredisait le plan juste
   * après une réservation ou une annulation (46 puis 45 libres pour 46 spots libres sur le plan, revue du 08/10).
   */
  const capacityTag = (() => {
    if (!plan || !cls) return null;
    if (kind === 'past' || kind === 'canceled') return null;
    const left = plan.freeSpotCount;
    if (classIsFull || left === 0) return { label: 'Complet', tone: 'red' as const };
    return { label: left > 1 ? `${left} spots libres` : `${left} spot libre`, tone: left <= 3 ? ('amber' as const) : ('green' as const) };
  })();

  const planHint: ReactNode = (() => {
    if (creditBlocked) return CREDIT_BLOCKED_HINT;
    if (selecting) return SELECT_SPOT_HINT;
    if (addingGuest) return 'Sélectionnez le spot de votre invité sur le plan.';
    if (kind === 'loggedOut') return 'Connectez-vous pour choisir votre spot.';
    return null;
  })();

  const planKicker = selecting || addingGuest ? 'Choisissez votre spot' : kind === 'booked' ? 'Votre spot' : 'Plan de salle';

  /* ------------------------------------------------- panneau : récapitulatif */

  const confirmLabel = (() => {
    if (kind === 'spotFreed') return 'Confirmer ma réservation';
    if (guestOn) return 'Confirmer les deux spots';
    if (!hasNoRoomLayout) return 'Confirmer mon spot';
    return 'Confirmer ma réservation';
  })();

  /*
   * Avant la réservation : solde actuel, coût, solde après (retours de Daphné du 14/10 : « Crédits restants 0 sur 1 »
   * dès l'arrivée sur la page, avant même d'avoir choisi son spot, se lisait comme le solde actuel).
   */
  const creditsLines = (count: number) =>
    freeOfCharge ? (
      <Line label="Crédits utilisés">{isRunClub ? 'Aucun (Run Club)' : 'Aucun (cours offert)'}</Line>
    ) : (
      <>
        {selectedService ? <Line label="Vos crédits">{remaining}</Line> : null}
        <Line label="Cette réservation">{plural(count, 'crédit')}</Line>
        {selectedService ? (
          <Line label="Après la réservation" total>
            {plural(Math.max(0, remaining - count), 'crédit')}
          </Line>
        ) : null}
      </>
    );

  let summary: ReactNode;
  if (kind === 'booked') {
    const memberSpot = mySpots.find((s) => !s.isGuest) ?? mySpots[0];
    const guestSpots = mySpots.filter((s) => s !== memberSpot);
    const placesBooked = Math.max(mySpots.length, bookedVisits.length, 1);
    summary = (
      <SummaryCard kicker="C’est réservé" booked session={session}>
        {memberSpot ? <Line label="Votre spot">#{memberSpot.spotNumber}</Line> : null}
        {guestSpots.map((s) => (
          <Line key={s.spotNumber} label="Spot de votre invité">
            #{s.spotNumber}
          </Line>
        ))}
        {!memberSpot ? <Line label="Votre place">Réservée</Line> : null}
        {freeOfCharge ? null : <Line label="Crédits utilisés">{creditsRequired * placesBooked}</Line>}
        {cls ? <p className="mmv2-tiny mmv2-muted mmv2-bp-note">{bookedCancelHint(new Date(cls.StartDateTime), lateCancelHours)}</p> : null}
      </SummaryCard>
    );
  } else if (kind === 'bookable' || kind === 'spotFreed') {
    summary = (
      <SummaryCard kicker="Votre réservation" session={session}>
        {!hasNoRoomLayout ? (
          <Line label="Spot">
            {selection.spot !== null ? `#${selection.spot}` : <span className="mmv2-muted mmv2-bp-todo">À choisir sur le plan</span>}
          </Line>
        ) : null}
        {guestOn ? (
          <Line label="Spot de l’invité">
            {selection.guestSpot !== null ? (
              `#${selection.guestSpot}`
            ) : (
              <span className="mmv2-muted mmv2-bp-todo">À choisir sur le plan</span>
            )}
          </Line>
        ) : null}
        {creditsLines(needed)}
        {showServicePicker ? (
          <ServicePicker services={services} selectedId={effectiveServiceId} needed={needed} onChange={setChosenServiceId} />
        ) : null}
      </SummaryCard>
    );
  } else if (kind === 'full') {
    summary = (
      <SummaryCard kicker="Votre réservation" session={session}>
        {showServicePicker && services.length > 1 ? (
          <ServicePicker services={services} selectedId={effectiveServiceId} needed={creditsRequired} onChange={setChosenServiceId} />
        ) : null}
      </SummaryCard>
    );
  } else if (kind === 'past') {
    summary = (
      <SummaryCard kicker="Ce cours est passé" session={session}>
        {/* La séance (date, heure) est juste au-dessus, dans ce panneau : pas de phrase qui la répète (revue du 05/10). */}
        {plan ? (
          <p className="mmv2-small mmv2-muted">Le plan de salle est affiché pour que vous voyiez comment elle était disposée.</p>
        ) : null}
        <button type="button" className="mmv2-btn mmv2-btn--primary mmv2-btn--block mmv2-class-gap" onClick={() => navigate('/')}>
          Trouver un prochain cours
          <Icon name="right" />
        </button>
      </SummaryCard>
    );
  } else {
    summary = session ? <SummaryCard kicker="Votre réservation" session={session} /> : null;
  }

  /* ------------------------------------------------- panneau : action */

  const errorNotice = booking.error ? (
    <Notice tone="warm" role="alert" focusAlert>
      <b>{booking.error.title}</b>
      {booking.error.description ? ` ${booking.error.description}` : null}
    </Notice>
  ) : null;

  const multiCreditNotice =
    isMultiCreditClass && kind !== 'booked' && kind !== 'past' ? (
      <Notice icon="info">
        <b>Session spéciale — {creditsRequired}&nbsp;crédits par réservation.</b> Cet événement consomme {creditsRequired}&nbsp;crédits sur
        votre forfait.
      </Notice>
    ) : null;

  const submitting = booking.isSubmitting;
  let action: ReactNode = null;
  let sticky = false;
  /** Bouton du dock (bureau) : même libellé, même action que celui de la carte d'action. */
  let dockButton: { label: string; onClick: () => void; disabled: boolean; icon: boolean } | null = null;
  /** Récapitulatif court de la barre collante et du dock (« Spot #12 · 1 crédit », « Invité #27 · 1 crédit »). */
  const recap =
    addingGuest && addGuest
      ? `${addGuest.spot !== null ? `Invité #${addGuest.spot}` : 'Invité'} · ${plural(creditsRequired, 'crédit')}`
      : `${selection.spot !== null ? `Spot #${selection.spot}` : ''}${guestOn && selection.guestSpot !== null ? ` + #${selection.guestSpot}` : ''}${
          freeOfCharge ? '' : ` · ${plural(needed, 'crédit')}`
        }`;
  /*
   * Barre d'action collante (spot choisi) : elle ne garde que le récapitulatif court, le bouton principal et le
   * secondaire. Encadrés (erreur, invité refusé, session spéciale) et lignes de détail passent dans une carte du fil de
   * la page, juste au-dessus d'elle (revue du 05/10 : barre de 394 px, 47 % de l'écran à 390, 24 spots sur 40 visibles
   * dans l'échec de l'invité).
   */
  let actionNotes: ReactNode = null;

  if (kind === 'bookable' || kind === 'spotFreed') {
    if (!enoughCredits && !submitting) {
      action = (
        <section className="mmv2-card mmv2-bp-sect mmv2-class-action" aria-label="Réserver">
          {multiCreditNotice}
          <Notice tone="warm">
            <b>Crédits insuffisants.</b> Il en faut {needed}, vous en avez {remaining}
            {selectedService ? ` sur le pack «\u00a0${selectedService.Name}\u00a0»` : ''}. Rien n’a été débité.
          </Notice>
          <button type="button" className="mmv2-btn mmv2-btn--cta mmv2-btn--lg mmv2-btn--block" onClick={buyCredits}>
            Acheter des crédits
            <Icon name="right" />
          </button>
        </section>
      );
    } else {
      sticky = barSticky;
      dockButton = {
        label: submitting ? 'Réservation en cours…' : confirmLabel,
        onClick: onConfirm,
        disabled: !!missingReason || submitting,
        icon: !submitting,
      };
      const notices =
        kind === 'spotFreed' || multiCreditNotice || errorNotice ? (
          <>
            {kind === 'spotFreed' ? (
              <Notice tone="green" role="status">
                <b>Un spot s’est libéré.</b> Choisissez-le sur le plan pour confirmer votre place.
              </Notice>
            ) : null}
            {multiCreditNotice}
            {errorNotice}
          </>
        ) : null;
      // Barre collante (une colonne) : les encadrés restent dans le fil de la page, au-dessus d'elle (voir actionNotes) ;
      // la barre garde une ligne courte quand la réservation échoue.
      if (sticky) actionNotes = notices;
      action = (
        <section className={`mmv2-card mmv2-bp-sect mmv2-class-action${sticky ? ' is-sticky' : ''}`} aria-label="Réserver">
          {sticky ? null : notices}
          <p className="mmv2-class-action__recap" aria-hidden="true">
            {recap}
          </p>
          {sticky && booking.error ? (
            <p className="mmv2-class-action__error" aria-hidden="true">
              Réservation non aboutie — détail ci-dessus.
            </p>
          ) : null}
          <button
            type="button"
            className="mmv2-btn mmv2-btn--primary mmv2-btn--lg mmv2-btn--block mmv2-class-confirm"
            aria-disabled={missingReason || submitting ? 'true' : undefined}
            aria-describedby={missingReason ? 'mmv2-class-reason' : undefined}
            onClick={onConfirm}
          >
            {submitting ? 'Réservation en cours…' : confirmLabel}
            {submitting ? null : <Icon name="right" />}
          </button>
          {missingReason ? (
            <p className="mmv2-class-reason" id="mmv2-class-reason">
              {missingReason}
            </p>
          ) : null}
          <p className="mmv2-tiny mmv2-muted mmv2-class-cancel-hint">{freeCancelHint(lateCancelHours)}</p>
        </section>
      );
    }
  } else if (kind === 'full') {
    action = (
      <section className="mmv2-card mmv2-bp-sect mmv2-class-action" aria-label="Liste d’attente">
        {multiCreditNotice}
        <Notice tone="warm">
          <b>Ce cours est complet.</b> Inscrivez-vous sur la liste d’attente&nbsp;: si un spot se libère, vous pourrez confirmer votre
          place sur cette page.
        </Notice>
        {errorNotice}
        <button
          type="button"
          className="mmv2-btn mmv2-btn--dark mmv2-btn--lg mmv2-btn--block"
          aria-disabled={submitting ? 'true' : undefined}
          onClick={submitting ? undefined : onJoinWaitlist}
        >
          <Icon name="hourglass" />
          {submitting ? 'Inscription…' : 'Rejoindre la liste d’attente'}
        </button>
      </section>
    );
  } else if (kind === 'waitlistFull') {
    action = (
      <section className="mmv2-card mmv2-bp-sect mmv2-class-action" aria-label="Liste d’attente">
        <Notice tone="warm">
          <b>Ce cours et sa liste d’attente sont complets.</b> Essayez un autre créneau.
        </Notice>
        <button type="button" className="mmv2-btn mmv2-btn--dark mmv2-btn--lg mmv2-btn--block" disabled aria-disabled="true">
          Liste d’attente complète
        </button>
        <button type="button" className="mmv2-btn mmv2-btn--quiet mmv2-btn--block mmv2-class-gap-sm" onClick={back}>
          Voir les autres cours
        </button>
      </section>
    );
  } else if (kind === 'waitlistJoined') {
    action = (
      <section className="mmv2-card mmv2-bp-sect mmv2-class-action" aria-label="Liste d’attente">
        <Notice tone="green" role="status">
          <b>Vous êtes sur la liste d’attente.</b> Si un spot se libère, revenez sur ce cours pour confirmer votre place.
        </Notice>
        {errorNotice}
        <button
          type="button"
          className="mmv2-btn mmv2-btn--ghost mmv2-btn--block"
          aria-disabled={submitting ? 'true' : undefined}
          onClick={submitting ? undefined : onLeaveWaitlist}
        >
          {leaving ? 'Un instant…' : 'Quitter la liste d’attente'}
        </button>
      </section>
    );
  } else if (kind === 'notOpen' && cls) {
    const opens = bookingOpensAt(cls.StartDateTime, Number(mondayMindbody?.customization?.numberOfDays ?? 8));
    action = (
      <section className="mmv2-card mmv2-bp-sect mmv2-class-action" aria-label="Réserver">
        <Notice>
          <b>Pas encore ouvert à la réservation.</b> Les réservations de ce cours ouvrent {opensLabel(opens)}.
        </Notice>
        {/* « Retour au planning » est déjà en tête du bandeau : ici, l'invitation à choisir un autre cours (revue du 05/10). */}
        <button type="button" className="mmv2-btn mmv2-btn--ghost mmv2-btn--block" onClick={back}>
          Voir d’autres cours
        </button>
      </section>
    );
  } else if (kind === 'canceled') {
    action = (
      <section className="mmv2-card mmv2-bp-sect mmv2-class-action" aria-label="Réserver">
        <Notice tone="warm">
          <b>Ce cours a été annulé.</b>
        </Notice>
        <button type="button" className="mmv2-btn mmv2-btn--primary mmv2-btn--block" onClick={() => navigate('/')}>
          Trouver un autre cours
          <Icon name="right" />
        </button>
      </section>
    );
  } else if (kind === 'noCredit') {
    action = (
      <section className="mmv2-card mmv2-bp-sect mmv2-class-action" aria-label="Réserver">
        <Notice tone="warm">
          <b>Crédits insuffisants.</b> Aucun de vos crédits ne permet de réserver cette séance&nbsp;: pack épuisé, expiré, pas encore actif
          ou valable dans un autre studio. Rien n’a été débité.
        </Notice>
        <button type="button" className="mmv2-btn mmv2-btn--cta mmv2-btn--lg mmv2-btn--block" onClick={buyCredits}>
          Acheter des crédits
          <Icon name="right" />
        </button>
      </section>
    );
  } else if (kind === 'booked') {
    const canAddGuest =
      !hasNoRoomLayout &&
      !isCourse &&
      !isEnrollmentInURL &&
      !isRunClub &&
      !isCommunityClass &&
      inWindow &&
      !classIsFull &&
      (plan?.freeSpotCount ?? 0) > 0;
    if (addingGuest && addGuest) {
      const retry = addGuest.retryAfter !== undefined;
      sticky = barSticky;
      dockButton = {
        label: submitting ? 'Réservation en cours…' : retry ? 'Réessayer pour l’invité' : 'Réserver le spot de l’invité',
        onClick: onConfirmGuest,
        disabled: addGuest.spot === null || submitting,
        icon: false,
      };
      const guestDetails = (
        <>
          {retry ? (
            // Sans le titre d'erreur générique de la v1 (« Nous n’avons pas pu vous inscrire à cette séance. »), qui
            // contredisait la 1re phrase : la place du client EST réservée (revue du 04/10).
            <Notice tone="warm" role="alert" focusAlert>
              <b>Votre spot #{addGuest.retryAfter} est réservé, mais pas celui de votre invité.</b> La place de votre
              invité n’a pas pu être réservée et aucun crédit n’a été débité pour elle. Réessayez, choisissez un autre
              spot sur le plan ou continuez sans invité.
            </Notice>
          ) : (
            errorNotice
          )}
          {!retry ? (
            <p className="mmv2-small mmv2-muted">
              Une place de plus est débitée de vos crédits ({plural(creditsRequired, 'crédit')}). Votre invité n’a pas besoin de compte.
            </p>
          ) : null}
          <div className="mmv2-class-summary__body">
            <Line label="Spot de l’invité">
              {addGuest.spot !== null ? `#${addGuest.spot}` : <span className="mmv2-muted mmv2-bp-todo">À choisir sur le plan</span>}
            </Line>
            {selectedService ? (
              <>
                <Line label="Vos crédits">{remaining}</Line>
                <Line label="Après l’ajout" total>
                  {plural(Math.max(0, remaining - creditsRequired), 'crédit')}
                </Line>
              </>
            ) : null}
          </div>
          {selectedService && remaining < creditsRequired ? (
            <>
              <Notice tone="warm">
                <b>Crédits insuffisants.</b> Il en faut {creditsRequired}, vous en avez {remaining}.
              </Notice>
              {/* Retours de Daphné du 14/10 : « je ne vois aucune option pour racheter des crédits à cette étape ». */}
              <p className="mmv2-small mmv2-class-guest-buy">
                <button type="button" className="mmv2-linkish" onClick={buyCredits}>
                  Acheter des crédits
                </button>
              </p>
            </>
          ) : null}
        </>
      );
      if (sticky) actionNotes = guestDetails;
      action = (
        <section className={`mmv2-card mmv2-bp-sect mmv2-class-action${sticky ? ' is-sticky' : ''}`} aria-label="Ajouter un invité">
          {sticky ? null : guestDetails}
          {/* Barre collante : récapitulatif court (« Invité #27 · 1 crédit »). */}
          <p className="mmv2-class-action__recap" aria-hidden="true">
            {recap}
          </p>
          {sticky && (retry || booking.error) ? (
            <p className="mmv2-class-action__error" aria-hidden="true">
              {retry ? 'Place de l’invité non réservée — détail ci-dessus.' : 'Réservation non aboutie — détail ci-dessus.'}
            </p>
          ) : null}
          <button
            type="button"
            className="mmv2-btn mmv2-btn--primary mmv2-btn--lg mmv2-btn--block mmv2-class-confirm"
            aria-disabled={addGuest.spot === null || submitting ? 'true' : undefined}
            aria-describedby={addGuest.spot === null ? 'mmv2-class-reason' : undefined}
            onClick={onConfirmGuest}
          >
            {submitting ? 'Réservation en cours…' : retry ? 'Réessayer pour l’invité' : 'Réserver le spot de l’invité'}
          </button>
          {addGuest.spot === null ? (
            <p className="mmv2-class-reason" id="mmv2-class-reason">
              Choisissez le spot de votre invité sur le plan.
            </p>
          ) : null}
          <button
            type="button"
            className="mmv2-btn mmv2-btn--quiet mmv2-btn--block mmv2-class-gap-sm"
            onClick={() => {
              booking.setError(null);
              setAddGuest(null);
              if (retry) goConfirmation(mySpots.map((s) => s.spotNumber), effectiveServiceId);
            }}
          >
            {retry ? 'Continuer sans invité' : 'Annuler'}
          </button>
        </section>
      );
    } else {
      action = (
        <section className="mmv2-card mmv2-bp-sect mmv2-class-action" aria-label="Gérer ma réservation">
          {errorNotice}
          <button type="button" className="mmv2-btn mmv2-btn--ghost mmv2-btn--block" onClick={addToCalendar}>
            <Icon name="cal" />
            Ajouter à mon agenda
          </button>
          {canAddGuest ? (
            <button
              type="button"
              className="mmv2-btn mmv2-btn--ghost mmv2-btn--block mmv2-class-gap-sm"
              onClick={() => {
                booking.setError(null);
                setAddGuest({ spot: null });
                window.setTimeout(focusPlan, 0);
              }}
            >
              <Icon name="users" />
              Ajouter un invité
            </button>
          ) : null}
          {bookedVisits.length ? (
            <button type="button" className="mmv2-btn mmv2-btn--quiet mmv2-btn--block mmv2-class-gap-sm mmv2-class-cancel" onClick={() => setCancelOpen(true)}>
              Annuler cette réservation
            </button>
          ) : (
            <button type="button" className="mmv2-btn mmv2-btn--quiet mmv2-btn--block mmv2-class-gap-sm" onClick={() => navigate('/account/visits')}>
              Voir toutes mes réservations
            </button>
          )}
        </section>
      );
    }
  }

  const guestBox = guestOffered ? (
    <GuestBox
      on={guestOn}
      disabledReason={guestDisabledReason}
      onBuyCredits={guestDisabledReason ? buyCredits : undefined}
      onToggle={() => {
        booking.setError(null);
        setSelection((s) => toggleGuest(s));
      }}
      guestSpot={selection.guestSpot}
      creditsRequired={creditsRequired}
    />
  ) : null;

  // Sans plan : le texte suit l'état (revue du 05/10 : « votre place est réservée » avant toute réservation).
  const noPlanText =
    kind === 'booked'
      ? 'Pas de plan de salle pour ce cours\u00a0: votre place est réservée, sans numéro de spot.'
      : kind === 'bookable' || kind === 'spotFreed' || kind === 'notOpen' || kind === 'noCredit'
        ? 'Pas de plan de salle pour ce cours\u00a0: votre place sera réservée sans numéro de spot.'
        : 'Pas de plan de salle pour ce cours.';

  const roomCard = plan ? (
    <RoomPlan
      ref={planRef}
      plan={plan}
      coachName={coachName(cls?.Staff)}
      kicker={planKicker}
      title={studio || 'Le studio'}
      hint={planHint}
      capacityTag={capacityTag}
      interactive={interactive}
      onSpot={onSpot}
      showGuestLegend={guestOffered || addingGuest}
      showSelfLegend={selecting}
      // Note du premier rang : seulement quand on choisit un spot (pas pour un cours passé, complet ou consulté déconnecté).
      frontRowNote={siteId === DYNAMO_PARIS_SITE_ID && !isEnrollmentInURL && interactive ? FRONT_ROW_NOTE : null}
    />
  ) : cls && kind !== 'loggedOut' ? (
    <section className="mmv2-card mmv2-card--pad mmv2-room-card mmv2-room-card--empty">
      <span className="mmv2-kicker">Plan de salle</span>
      <p className="mmv2-small mmv2-muted">{noPlanText}</p>
    </section>
  ) : null;

  const cancellableVisits = kind === 'booked' && bookedVisits.length ? bookedVisits : [];
  // Places réservées (la sienne, celle de l'invité), avec leur spot : annulables une à une (= v1).
  const seatsToCancel = cancellableVisits.length ? cancelSeats(cancellableVisits, pick?.reservations) : [];

  return (
    <div
      className="mmv2-class-page"
      style={colorVars as CSSProperties}
      data-panel={kind}
      data-credit={creditBlocked ? 'none' : undefined}
    >
      <Hero kicker={heroKicker} title={heroTitle} subtitle={cls?.ThemeName} />

      <div className={`mmv2-class-layout${sticky ? ' has-sticky' : ''}`}>
        <div className="mmv2-class-main">
          {kind === 'loggedOut' ? (
            <section className="mmv2-card mmv2-card--pad mmv2-class-auth mmv2-class-slot--auth" aria-labelledby="mmv2-class-auth-title">
              <span className="mmv2-kicker">Connexion</span>
              <h2 className="mmv2-h3 mmv2-class-auth__title" id="mmv2-class-auth-title">
                Connectez-vous pour réserver
              </h2>
              <p className="mmv2-small mmv2-muted mmv2-class-auth__text">Une fois connecté, vous revenez sur ce cours pour choisir votre spot.</p>
              {/* Même habillage que la carte de connexion de Mon compte (account.css, .mmv2-acct-auth). */}
              <div className="mmv2-acct mmv2-class-auth__form" ref={authRef}>
                <div className="mmv2-acct-auth mmv2-acct-auth--plain">
                  <AuthScreen />
                </div>
              </div>
            </section>
          ) : null}
          {roomCard ? <div className="mmv2-class-slot--plan">{roomCard}</div> : null}
          {cls ? (
            <div className="mmv2-class-slot--course">
              <CourseCard cls={cls} />
            </div>
          ) : null}
        </div>

        <aside className="mmv2-class-aside" aria-label="Votre réservation" ref={asideRef}>
          {summary ? <div className="mmv2-class-slot--summary">{summary}</div> : null}
          {guestBox ? <div className="mmv2-class-slot--guest">{guestBox}</div> : null}
          {actionNotes ? (
            <div className="mmv2-class-slot--notes">
              <section className="mmv2-card mmv2-bp-sect mmv2-class-action mmv2-class-notes" aria-label="Détails de la réservation">
                {actionNotes}
              </section>
            </div>
          ) : null}
          {action ? (
            <div className={`mmv2-class-slot--action${sticky ? ' is-sticky' : ''}`} ref={actionSlotRef}>
              {action}
            </div>
          ) : null}
        </aside>
      </div>

      {dockWanted && dockButton ? (
        // Rappel du bureau, pour la souris : la carte d'action du panneau reste la cible du clavier et des lecteurs d'écran.
        <div className="mmv2-class-dock" ref={dockRef} aria-hidden="true" data-shown={dockShown ? '' : undefined}>
          <p className="mmv2-class-dock__recap">{recap}</p>
          <button
            type="button"
            tabIndex={-1}
            className="mmv2-btn mmv2-btn--primary mmv2-btn--lg mmv2-btn--block mmv2-class-dock__confirm"
            aria-disabled={dockButton.disabled ? 'true' : undefined}
            onClick={dockButton.onClick}
          >
            {dockButton.label}
            {dockButton.icon ? <Icon name="right" /> : null}
          </button>
        </div>
      ) : null}

      {cls && cancellableVisits.length ? (
        <CancelModal
          isOpen={cancelOpen}
          onClose={() => setCancelOpen(false)}
          seats={seatsToCancel}
          lateCancelHours={lateCancelHours}
          summary={`${cls.ClassDescription?.Name ?? 'Cours'} à ${studio}, ${longDate(new Date(cls.StartDateTime)).toLowerCase()} à ${hour(
            new Date(cls.StartDateTime),
          )}.`}
          onCancelled={() => {
            setSelection(EMPTY_SELECTION);
            classQuery.refetch();
          }}
        />
      ) : null}
    </div>
  );
}

function ClassLoading({ style }: { style: Record<string, string> }) {
  return (
    <div className="mmv2-class-page" style={style as CSSProperties} aria-busy="true">
      <header className="mmv2-class-hero">
        <Skeleton h="14px" w="160px" startColor="whiteAlpha.300" endColor="whiteAlpha.100" />
        <Skeleton h="44px" w="min(420px, 80%)" mt="18px" startColor="whiteAlpha.300" endColor="whiteAlpha.100" />
      </header>
      <div className="mmv2-class-layout">
        <div className="mmv2-class-main">
          <div className="mmv2-card mmv2-card--pad">
            <Skeleton h="14px" w="140px" />
            <Skeleton mt="24px" w="full" pb="52%" />
          </div>
        </div>
        <aside className="mmv2-class-aside">
          <div className="mmv2-card mmv2-bp-sect">
            <Skeleton h="12px" w="120px" />
            <Skeleton h="80px" mt="16px" />
          </div>
        </aside>
      </div>
      <span className="mmv2-visually-hidden" role="status">
        Chargement du cours…
      </span>
    </div>
  );
}

